/**
 * React Query key factory. Keys are arrays so prefixes invalidate whole families:
 * `keys.teacher.galleyAll()` covers every galley detail, `keys.world.galley(id)` covers the
 * world and every piece of one galley. Family tokens are part of world keys so a family
 * viewer never shares cache entries with a signed-in viewer.
 */
export const keys = {
  session: () => ['session'] as const,

  teacher: {
    all: () => ['t'] as const,
    galleys: () => ['t', 'galleys'] as const,
    deletedGalleys: () => ['t', 'galleys', 'deleted'] as const,
    courses: () => ['t', 'courses'] as const,
    galleyAll: () => ['t', 'galley'] as const,
    galley: (galleyId: string) => ['t', 'galley', galleyId] as const,
    coursework: (galleyId: string) => ['t', 'coursework', galleyId] as const,
    cardsAll: () => ['t', 'cards'] as const,
    cards: (galleyId: string) => ['t', 'cards', galleyId] as const,
    familyAll: () => ['t', 'family'] as const,
    family: (galleyId: string) => ['t', 'family', galleyId] as const,
    roomAll: () => ['t', 'room'] as const,
    room: (roomId: string) => ['t', 'room', roomId] as const,
    walkAll: () => ['t', 'walk'] as const,
    walk: (walkId: string) => ['t', 'walk', walkId] as const,
    reviewAll: () => ['t', 'review'] as const,
    review: (walkId: string) => ['t', 'review', walkId] as const,
    debrief: (walkId: string) => ['t', 'debrief', walkId] as const,
  },

  world: {
    all: () => ['g'] as const,
    galley: (galleyId: string) => ['g', galleyId] as const,
    /** `view` is the viewer perspective (see world/viewer.ts): 'student', 'projector', 'f:<token>' or null. */
    world: (galleyId: string, view?: string | null) =>
      ['g', galleyId, 'world', view ?? null] as const,
    piece: (galleyId: string, pieceId: string, view?: string | null) =>
      ['g', galleyId, 'piece', pieceId, view ?? null] as const,
  },

  student: {
    all: () => ['s'] as const,
    me: () => ['s', 'me'] as const,
    walkReturn: (walkId: string) => ['s', 'return', walkId] as const,
  },

  join: {
    choices: () => ['join', 'choices'] as const,
  },

  family: {
    view: (token: string) => ['f', token] as const,
  },

  instance: {
    kit: () => ['instance', 'kit'] as const,
  },
}
